// public/js/features/alerts/alert-wizard.js
// Create/edit an alert via a single modal whose sections follow the natural
// order: 1) metric → 2) condition → 3) behaviour → 4) assets & source. Uses the
// app's standard light theme (like every other modal) so help "?" icons and
// text read with normal contrast.
import { ALERT_METRICS, ALERT_OPERATORS, metricByKey, createAlert, updateAlert } from './api.js';
import { formatAssetLabel } from '../../utils/asset-utils.js';
import { helpIcon, initHelp } from '../../ui/help.js';
import { loadTenants } from '../../modules/tenants.js';

// Derived-sensor rules (tenant.sensorRules) become extra alert metrics: a 0/1
// state you can alert on (e.g. "Load state == 1"). Collected across the user's
// tenants, de-duped by derivedKey. Returns [{ key, label }].
async function collectDerivedMetrics() {
  try {
    const tenants = await loadTenants();
    const byKey = new Map();
    for (const t of tenants || []) {
      for (const r of (Array.isArray(t.sensorRules) ? t.sensorRules : [])) {
        if (r?.derivedKey && !byKey.has(r.derivedKey)) byKey.set(r.derivedKey, r.label || r.derivedKey);
      }
    }
    return [...byKey.entries()].map(([key, label]) => ({ key, label }));
  } catch { return []; }
}

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// In-app help for the alert wizard — see docs/help-text.md.
const HELP = {
  condition:
    '<b>Condition — when does it fire?</b><br>' +
    'Pick an operator and a value; the alert fires when the metric crosses it. <i>Outside / Inside</i> take a ' +
    'range (two values).' +
    '<br><br><b>Aviation:</b> cargo-hold temperature <b>above 25 °C</b> warns of a cooling failure; cabin ' +
    'pressure <b>outside 900–1050 hPa</b> flags a sealing problem.',
  geofence:
    '<b>Geofence direction</b><br>' +
    'Fire when the asset <b>enters</b> the fence, <b>exits</b> it, or <b>both</b>. The fence itself (circle or ' +
    'polygon) is set on the POI.' +
    '<br><br><b>Aviation:</b> “on exit” of the hangar fence tells you the moment an aircraft is towed out.',
  behaviour:
    '<b>Behaviour — what happens after it fires?</b><br>' +
    '<b>Re-arm after acknowledgement:</b> stays quiet until someone acknowledges, then watches again (no spam). ' +
    '<b>Re-arm after a time:</b> re-arms automatically after N minutes. <b>Fire once:</b> alerts a single time, ' +
    'then never again.' +
    '<br><br><b>Aviation:</b> an over-temperature alert set to “acknowledge” won’t re-nag every minute while a ' +
    'mechanic is already on it.',
  hysteresis:
    '<b>Hysteresis — anti-flapping margin</b><br>' +
    'A dead-band around the threshold so a value sitting right on it doesn’t alert over and over: it must ' +
    'recover by this much before it can fire again. 0 = off.' +
    '<br><br><b>Aviation:</b> threshold 25 °C, hysteresis 2 → fires at 25, re-arms only after it drops below 23, ' +
    'so a sensor wobbling 24.9 / 25.1 stays quiet.',
  consecutive:
    '<b>Consecutive readings</b><br>' +
    'How many readings in a row must breach before it fires. Higher = fewer false alarms from a single noisy ' +
    'sample, at the cost of a slightly later warning.' +
    '<br><br><b>Aviation:</b> require 3 consecutive high-vibration reads so one bump during towing doesn’t raise ' +
    'an alert.',
  cooldown:
    '<b>Cooldown (minutes)</b><br>' +
    'After firing, stay silent for at least this long before the same alert may fire again — a floor on how ' +
    'often it can repeat.' +
    '<br><br><b>Aviation:</b> a 60-min cooldown on a door-open alert avoids a burst while the hold is loaded.',
  autoresolve:
    '<b>Auto-resolve</b><br>' +
    'On: the alert clears itself once the value returns to normal — no manual dismiss. Off: it stays open until ' +
    'someone acknowledges it.' +
    '<br><br><b>Aviation:</b> a temperature alert can auto-resolve when the hold cools back down; a security ' +
    'alert you may prefer to clear by hand.',
};

// Which devices of an asset report a given (snapshot) metric → for source choice.
function sourcesFor(asset, metric) {
  const map = asset?.lastSensors;
  if (!map || typeof map !== 'object') return [];
  return Object.values(map)
    .filter(d => d && d[metric] != null)
    .map(d => ({ deviceId: d.deviceId, label: d.subType || d.type || d.deviceId }));
}

export function openAlertWizard({ alert = null, assets = [], onSaved } = {}) {
  const editing = !!alert;
  const el = document.createElement('div');
  el.className = 'modal fade';
  el.tabIndex = -1;
  el.innerHTML = `
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="fa-solid fa-bell me-2"></i>${editing ? 'Edit alert' : 'New alert'}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label fw-bold">1. What to monitor</label>
            <select id="aw-metric" class="form-select">
              ${ALERT_METRICS.map(m => `<option value="${m.key}">${esc(m.label)}${m.unit ? ` (${m.unit})` : ''}</option>`).join('')}
            </select>
          </div>

          <div class="mb-3 d-none" id="aw-geo-section">
            <label class="form-label fw-bold">2. Geofence${helpIcon('geofence')}</label>
            <div class="d-flex gap-2 align-items-center flex-wrap">
              <select id="aw-geo-poi" class="form-select" style="max-width:320px;">
                <option value="">Loading POIs…</option>
              </select>
              <select id="aw-geo-dir" class="form-select" style="max-width:200px;">
                <option value="enter">on enter</option>
                <option value="exit">on exit</option>
                <option value="both" selected>enter &amp; exit</option>
              </select>
            </div>
            <div class="form-text text-primary" id="aw-geo-note"></div>
          </div>

          <div class="mb-3" id="aw-cond-section">
            <label class="form-label fw-bold">2. Condition${helpIcon('condition')}</label>
            <div class="d-flex gap-2 align-items-center flex-wrap">
              <select id="aw-op" class="form-select" style="max-width:220px;">
                ${ALERT_OPERATORS.map(o => `<option value="${o.op}">${esc(o.label)}</option>`).join('')}
              </select>
              <input id="aw-th" type="number" step="any" class="form-control" style="max-width:140px;" placeholder="value">
              <span id="aw-th2-wrap" class="d-none d-flex align-items-center gap-2">
                <span>and</span>
                <input id="aw-th2" type="number" step="any" class="form-control" style="max-width:140px;" placeholder="value 2">
              </span>
              <span id="aw-unit" class="text-muted"></span>
            </div>
            <div class="form-text text-primary" id="aw-preview"></div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-bold">3. Behaviour${helpIcon('behaviour')}</label>
            <div class="row g-2">
              <div class="col-md-7">
                <select id="aw-behavior" class="form-select">
                  <option value="reactivate_ack">Re-arm after acknowledgement</option>
                  <option value="reactivate_time">Re-arm after a time</option>
                  <option value="once">Fire once</option>
                </select>
              </div>
              <div class="col-md-5">
                <select id="aw-severity" class="form-select">
                  <option value="info">Info</option>
                  <option value="warning" selected>Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div id="aw-reactivate-wrap" class="mt-2 d-none">
              <label class="form-label small">Re-arm after (minutes)</label>
              <input id="aw-reactivate-min" type="number" min="1" class="form-control form-control-sm" style="max-width:160px;" value="60">
            </div>
            <details class="mt-2">
              <summary class="small text-muted" style="cursor:pointer;">Advanced</summary>
              <div class="row g-2 mt-1">
                <div class="col-6 col-md-3"><label class="form-label small">Hysteresis${helpIcon('hysteresis')}</label><input id="aw-hyst" type="number" step="any" min="0" class="form-control form-control-sm" value="0"></div>
                <div class="col-6 col-md-3"><label class="form-label small">Consecutive${helpIcon('consecutive')}</label><input id="aw-consec" type="number" min="1" class="form-control form-control-sm" value="1"></div>
                <div class="col-6 col-md-3"><label class="form-label small">Cooldown (min)${helpIcon('cooldown')}</label><input id="aw-cooldown" type="number" min="0" class="form-control form-control-sm" value="15"></div>
                <div class="col-6 col-md-3 d-flex align-items-end"><div class="form-check"><input id="aw-autoresolve" type="checkbox" class="form-check-input" checked><label class="form-check-label small" for="aw-autoresolve">Auto-resolve${helpIcon('autoresolve')}</label></div></div>
              </div>
            </details>
          </div>

          <div class="mb-2">
            <label class="form-label fw-bold">4. Assets</label>
            <div class="btn-group btn-group-sm d-flex mb-2" role="group">
              <input type="radio" class="btn-check" name="aw-scope" id="aw-scope-sel" value="assets" checked>
              <label class="btn btn-outline-primary" for="aw-scope-sel">Selected assets</label>
              <input type="radio" class="btn-check" name="aw-scope" id="aw-scope-all" value="all">
              <label class="btn btn-outline-primary" for="aw-scope-all">All my assets</label>
            </div>
            <input id="aw-asset-search" class="form-control form-control-sm mb-2" placeholder="Search assets…">
            <div id="aw-asset-list" style="max-height:240px;overflow:auto;border:1px solid #dee2e6;border-radius:8px;padding:6px;"></div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button id="aw-save" type="button" class="btn btn-success"><i class="fa-solid fa-check me-1"></i>${editing ? 'Save' : 'Create alert'}</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(el);
  initHelp(el, HELP); // wire the "?" help icons

  const $ = (sel) => el.querySelector(sel);
  const modal = bootstrap.Modal.getOrCreateInstance(el);
  el.addEventListener('hidden.bs.modal', () => el.remove());

  // Derived-sensor metrics (tenant.sensorRules) → label lookup for the preview.
  // Populated async below; injected into the metric dropdown as their own group.
  const derivedByKey = {};

  // ── prefill (edit) ──
  if (editing) {
    // A derived-key alert's option isn't in the static catalog — inject a stub so
    // the select can hold it (the async loader replaces it with the real label).
    if (alert.metric && !metricByKey(alert.metric)) {
      const stub = document.createElement('option');
      stub.value = alert.metric;
      stub.textContent = alert.metric;
      $('#aw-metric').appendChild(stub);
      derivedByKey[alert.metric] = { key: alert.metric, label: alert.metric, unit: '' };
    }
    $('#aw-metric').value = alert.metric;
    if (alert.metric === 'geofence' && alert.direction) $('#aw-geo-dir').value = alert.direction;
    if (alert.operator) $('#aw-op').value = alert.operator;
    $('#aw-th').value = alert.threshold ?? '';
    if (alert.threshold2 != null) $('#aw-th2').value = alert.threshold2;
    $('#aw-behavior').value = alert.behavior || 'reactivate_ack';
    $('#aw-severity').value = alert.severity || 'warning';
    if (alert.reactivateAfterMin) $('#aw-reactivate-min').value = alert.reactivateAfterMin;
    $('#aw-hyst').value = alert.hysteresis ?? 0;
    $('#aw-consec').value = alert.consecutive ?? 1;
    $('#aw-cooldown').value = alert.cooldownMin ?? 0;
    $('#aw-autoresolve').checked = alert.autoResolve !== false;
    if (alert.scope === 'all') $('#aw-scope-all').checked = true;
  }

  const selectedTargets = new Map(); // assetUUID → deviceId|'any'
  if (editing && Array.isArray(alert.targets)) {
    alert.targets.forEach(t => selectedTargets.set(t.assetUUID, t.deviceId || 'any'));
  }

  // ── Geofence: lazily loaded POI list + fence note ──
  let pois = [];
  let poisLoaded = false;
  async function loadPois() {
    if (poisLoaded) return;
    poisLoaded = true;
    try {
      const res = await window.authenticatedFetch('api/v1/pois');
      pois = ((await res.json()).pois || []).filter(p => p.lat != null && p.lng != null);
    } catch { pois = []; }
    const sel = $('#aw-geo-poi');
    sel.innerHTML = pois.length
      ? `<option value="">— select a POI —</option>` + pois
        .sort((a, b) => String(a.name).localeCompare(String(b.name)))
        .map(p => `<option value="${esc(p.poiId)}">${esc(p.name || p.poiId)}</option>`).join('')
      : `<option value="">No POIs available — create one on the map first</option>`;
    if (editing && alert.metric === 'geofence' && alert.poiId) sel.value = alert.poiId;
    updateGeoNote();
  }
  function updateGeoNote() {
    const p = pois.find(x => x.poiId === $('#aw-geo-poi').value);
    $('#aw-geo-note').textContent = !p ? ''
      : (Array.isArray(p.geofencePolygon) && p.geofencePolygon.length >= 3
        ? `Fence: custom polygon (${p.geofencePolygon.length} points)`
        : Number(p.geofenceRadiusM) > 0
          ? `Fence radius: ${p.geofenceRadiusM} m (set on the POI)`
          : 'This POI has no geofence radius yet — the default of 500 m applies.');
  }

  function updatePreview() {
    const key = $('#aw-metric').value;
    const m = metricByKey(key) || derivedByKey[key];
    const isGeo = key === 'geofence';
    $('#aw-cond-section').classList.toggle('d-none', isGeo);
    $('#aw-geo-section').classList.toggle('d-none', !isGeo);
    if (isGeo) { loadPois(); $('#aw-preview').textContent = ''; return; }
    const op = $('#aw-op').value;
    const range = (op === 'outside' || op === 'inside');
    $('#aw-th2-wrap').classList.toggle('d-none', !range);
    $('#aw-unit').textContent = m?.unit || '';
    const th = $('#aw-th').value;
    const th2 = $('#aw-th2').value;
    const opLabel = ALERT_OPERATORS.find(o => o.op === op)?.label || op;
    $('#aw-preview').textContent = th !== '' ? `Alert when ${m?.label} ${opLabel} ${th}${range && th2 !== '' ? `..${th2}` : ''} ${m?.unit || ''}` : '';
  }

  function renderAssetList() {
    const all = $('#aw-scope-all').checked;
    const wrap = $('#aw-asset-list');
    $('#aw-asset-search').classList.toggle('d-none', all);
    if (all) { wrap.innerHTML = `<div class="text-muted small p-2">Applies to every asset in your tenant(s).</div>`; return; }
    const metric = $('#aw-metric').value;
    const term = $('#aw-asset-search').value.trim().toLowerCase();
    const list = assets
      .filter(a => !term || formatAssetLabel(a).toLowerCase().includes(term))
      .slice(0, 300);
    wrap.innerHTML = list.length ? list.map(a => {
      const id = a.assetUUID;
      const checked = selectedTargets.has(id);
      const srcs = sourcesFor(a, metric);
      const sel = selectedTargets.get(id) || 'any';
      const srcDropdown = (checked && srcs.length > 1)
        ? `<select class="form-select form-select-sm aw-src ms-2" data-asset="${id}" style="max-width:160px;">
             <option value="any"${sel === 'any' ? ' selected' : ''}>any source</option>
             ${srcs.map(s => `<option value="${esc(s.deviceId)}"${sel === s.deviceId ? ' selected' : ''}>${esc(s.label)}</option>`).join('')}
           </select>` : '';
      return `<div class="d-flex align-items-center py-1">
          <input class="form-check-input m-0 me-2 aw-asset" type="checkbox" data-asset="${id}" ${checked ? 'checked' : ''}>
          <span class="flex-grow-1 text-truncate">${esc(formatAssetLabel(a))}</span>
          ${srcDropdown}
        </div>`;
    }).join('') : `<div class="text-muted small p-2">No assets.</div>`;

    wrap.querySelectorAll('.aw-asset').forEach(cb => cb.addEventListener('change', () => {
      const id = cb.dataset.asset;
      if (cb.checked) selectedTargets.set(id, selectedTargets.get(id) || 'any');
      else selectedTargets.delete(id);
      renderAssetList();
    }));
    wrap.querySelectorAll('.aw-src').forEach(s => s.addEventListener('change', () => {
      selectedTargets.set(s.dataset.asset, s.value);
    }));
  }

  $('#aw-metric').addEventListener('change', () => { updatePreview(); renderAssetList(); });
  $('#aw-geo-poi').addEventListener('change', updateGeoNote);
  $('#aw-op').addEventListener('change', updatePreview);
  $('#aw-th').addEventListener('input', updatePreview);
  $('#aw-th2').addEventListener('input', updatePreview);
  $('#aw-behavior').addEventListener('change', () => {
    $('#aw-reactivate-wrap').classList.toggle('d-none', $('#aw-behavior').value !== 'reactivate_time');
  });
  el.querySelectorAll('input[name="aw-scope"]').forEach(r => r.addEventListener('change', renderAssetList));
  $('#aw-asset-search').addEventListener('input', renderAssetList);

  updatePreview();
  $('#aw-reactivate-wrap').classList.toggle('d-none', $('#aw-behavior').value !== 'reactivate_time');
  renderAssetList();
  modal.show();

  // Append the tenant's derived-sensor metrics as their own group. Async so the
  // modal opens instantly; the current selection is preserved.
  collectDerivedMetrics().then(list => {
    if (!list.length) return;
    const sel = $('#aw-metric');
    const keep = sel.value;
    // Drop the edit-stub (real option comes from the group below).
    if (editing && derivedByKey[keep]) {
      sel.querySelector(`option[value="${CSS.escape(keep)}"]`)?.remove();
    }
    const group = document.createElement('optgroup');
    group.label = 'Derived sensors';
    for (const d of list) {
      derivedByKey[d.key] = { key: d.key, label: d.label, unit: '' };
      const opt = document.createElement('option');
      opt.value = d.key;
      opt.textContent = d.label;
      group.appendChild(opt);
    }
    sel.appendChild(group);
    sel.value = keep;               // restore selection (edit or default)
    updatePreview();
  });

  // Picking a derived metric prefills the natural "state == 1" condition.
  $('#aw-metric').addEventListener('change', () => {
    const key = $('#aw-metric').value;
    if (derivedByKey[key] && !editing) {
      $('#aw-op').value = '==';
      if ($('#aw-th').value === '') $('#aw-th').value = '1';
      updatePreview();
    }
  });

  $('#aw-save').addEventListener('click', async () => {
    const scope = $('#aw-scope-all').checked ? 'all' : 'assets';
    const metric = $('#aw-metric').value;
    const isGeo = metric === 'geofence';

    let op = null, th = null, th2 = null, poiId = null, poiName = null, direction = null;
    if (isGeo) {
      poiId = $('#aw-geo-poi').value;
      if (!poiId) { window.showToast('Select a POI for the geofence', 'warning'); return; }
      poiName = pois.find(p => p.poiId === poiId)?.name || null;
      direction = $('#aw-geo-dir').value;
    } else {
      op = $('#aw-op').value;
      th = parseFloat($('#aw-th').value);
      if (Number.isNaN(th)) { window.showToast('Enter a threshold value', 'warning'); return; }
      const range = (op === 'outside' || op === 'inside');
      th2 = range ? parseFloat($('#aw-th2').value) : null;
      if (range && Number.isNaN(th2)) { window.showToast('Enter the second value for the range', 'warning'); return; }
    }
    const targets = scope === 'assets'
      ? [...selectedTargets.entries()].map(([assetUUID, deviceId]) => ({ assetUUID, deviceId }))
      : [];
    if (scope === 'assets' && !targets.length) { window.showToast('Select at least one asset', 'warning'); return; }

    const payload = {
      metric,
      operator: op,
      threshold: th,
      threshold2: th2,
      poiId, poiName, direction,
      scope, targets,
      behavior: $('#aw-behavior').value,
      reactivateAfterMin: $('#aw-behavior').value === 'reactivate_time' ? parseInt($('#aw-reactivate-min').value) || 60 : null,
      hysteresis: parseFloat($('#aw-hyst').value) || 0,
      consecutive: parseInt($('#aw-consec').value) || 1,
      cooldownMin: parseInt($('#aw-cooldown').value) || 0,
      autoResolve: $('#aw-autoresolve').checked,
      severity: $('#aw-severity').value,
      channels: ['browser', 'push'], // push delivered only to subscribed devices
    };

    const btn = $('#aw-save');
    btn.disabled = true;
    btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>Saving…`;
    try {
      if (editing) await updateAlert(alert.alertId, payload);
      else await createAlert(payload);
      window.showToast(editing ? 'Alert updated' : 'Alert created', 'success');
      modal.hide();
      onSaved?.();
    } catch (e) {
      console.error(e);
      window.showToast(`Save failed: ${e.message}`, 'danger');
      btn.disabled = false;
      btn.innerHTML = `<i class="fa-solid fa-check me-1"></i>${editing ? 'Save' : 'Create alert'}`;
    }
  });
}
